/* global globalThis */
// Gameplay persistence is conditional on a verified, active research receipt.
// Withdrawal credentials are deliberately independent and must remain usable.
const IDENTITY_KEY = "pure_collection_game_identity_v1";
let consentReader = () => ({ allowed: false, participantId: "" });
const temporary = new Map();
let temporaryIdentity;

const durable = () => {
    try { return globalThis.localStorage; } catch { return null; }
};
const governanceKey = key => key.startsWith("pure_collection_") || key.startsWith("pureversation_consent");
const permission = () => {
    try {
        const value = consentReader();
        return value?.allowed === true && /^participant_[0-9a-f]{32}$/.test(value.participantId || "") ? value : null;
    } catch { return null; }
};
export const configureGameIdentityConsent = reader => { consentReader = reader; };
const ownsPersistedGame = () => {
    const consent = permission();
    if (!consent) return false;
    try { return JSON.parse(durable()?.getItem(IDENTITY_KEY) || "null")?.participantId === consent.participantId; }
    catch { return false; }
};

export const gameStorage = {
    getItem(key) {
        if (governanceKey(key) || ownsPersistedGame()) {
            try { return durable()?.getItem(key) ?? null; } catch { return null; }
        }
        return temporary.get(key) ?? null;
    },
    setItem(key, value) {
        if (governanceKey(key) || ownsPersistedGame()) {
            try { durable()?.setItem(key, String(value)); } catch { /* Nonessential game persistence. */ }
        } else temporary.set(key, String(value));
    },
    removeItem(key) {
        if (governanceKey(key) || ownsPersistedGame()) {
            try { durable()?.removeItem(key); } catch { /* Best effort; receipt gates writes. */ }
        }
        temporary.delete(key);
    },
    key(index) { return ownsPersistedGame() ? durable()?.key(index) ?? null : [...temporary.keys()][index] ?? null; },
    get length() { return ownsPersistedGame() ? durable()?.length || 0 : temporary.size; },
};

const randomHex = length => {
    const bytes = new Uint8Array(length);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
};
const freshIdentity = () => ({ sessionID: `session_${randomHex(16)}`, opAddr: `0x${randomHex(20)}` });
const validIdentity = value => /^session_[a-zA-Z0-9_-]+$/.test(value?.sessionID || "") && /^0x[0-9a-fA-F]{40}$/.test(value?.opAddr || "");

export const resolveGameIdentity = () => {
    const consent = permission();
    if (!consent) {
        temporaryIdentity ||= freshIdentity();
        return { ...temporaryIdentity, persistent: false };
    }
    const storage = durable();
    let identity;
    try { identity = JSON.parse(storage?.getItem(IDENTITY_KEY) || "null"); } catch { /* Invalid legacy entry. */ }
    if (identity?.participantId !== consent.participantId || !validIdentity(identity)) {
        // A pre-migration pseudonym may be reused only after verified consent.
        let legacy = null;
        try { if (!identity) legacy = { sessionID: storage?.getItem("pureversation_session_id"), opAddr: storage?.getItem("pureversation_operator_addr") }; } catch { /* Unavailable storage. */ }
        if (identity) forgetGameIdentity();
        identity = { ...(validIdentity(legacy) ? legacy : freshIdentity()), participantId: consent.participantId };
    }
    try {
        storage?.setItem(IDENTITY_KEY, JSON.stringify(identity));
        storage?.setItem("pureversation_session_id", identity.sessionID);
        storage?.setItem("pureversation_operator_addr", identity.opAddr);
        storage?.setItem("pure_identity_type", "anonymous");
        return { ...identity, persistent: storage?.getItem(IDENTITY_KEY) === JSON.stringify(identity) };
    } catch {
        temporaryIdentity ||= freshIdentity();
        return { ...temporaryIdentity, persistent: false };
    }
};

export const forgetGameIdentity = () => {
    const removable = [IDENTITY_KEY, "pure_collection_private_audio_receipt_v1"];
    const storage = durable();
    for (let index = 0; index < (storage?.length || 0); index++) {
        const key = storage.key(index);
        if (key && !governanceKey(key)) removable.push(key);
    }
    for (const key of removable) {
        try { durable()?.removeItem(key); } catch { /* Permission checks still prevent reuse. */ }
    }
    temporary.clear();
    temporaryIdentity = undefined;
};
